import { FC, memo, useMemo } from 'react'
import { Box } from '@mui/material'
import {
  Viewer,
  Worker,
  SpecialZoomLevel,
} from '@react-pdf-viewer/core'
import { SelectionMode } from '@react-pdf-viewer/selection-mode'
import { defaultLayoutPlugin, ToolbarSlot } from '@react-pdf-viewer/default-layout'
import { styled } from '@mui/material/styles'
import '@react-pdf-viewer/core/lib/styles/index.css'
import '@react-pdf-viewer/default-layout/lib/styles/index.css'

interface AccessiblePDFViewerProps {
  domNode: any,
  fileName?: string
  height?: string
}

const Container = styled(Box)<{ height: string }>(({ height }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height,
  '& .rpv-core__viewer': {
    width: '100%',
    height: '100%',
  },
}))

const Toolbar = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  padding: '0 16px',
  overflowX: "auto",
  overflowY: "hidden"
})

const ToolbarGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const PageSeparator = styled('span')({
  padding: '0px',
})

const AccessiblePDFViewer: FC<AccessiblePDFViewerProps> = memo(
  ({ domNode, fileName: fileNameProp = 'PDF document', height = '800px' }) => {
    const fileName = domNode.attribs?.['data-filename'] || fileNameProp

    const defaultLayoutPluginInstance = defaultLayoutPlugin({
      sidebarTabs: defaultTabs => [defaultTabs[0]],
      renderToolbar: ToolbarComponent => (
        <ToolbarComponent>
          {(slots: ToolbarSlot) => {
            const {
              CurrentPageInput,
              GoToNextPage,
              GoToPreviousPage,
              NumberOfPages,
              ZoomIn,
              ZoomOut,
              Zoom,
              SwitchSelectionMode,
            } = slots
            return (
              <Toolbar>
                <ToolbarGroup>
                  <GoToPreviousPage />
                  <CurrentPageInput />
                  <PageSeparator>/</PageSeparator>
                  <NumberOfPages />
                  <GoToNextPage />
                </ToolbarGroup>
                <ToolbarGroup>
                  <ZoomOut />
                  <Zoom />
                  <ZoomIn />
                </ToolbarGroup>
                <ToolbarGroup>
                  <SwitchSelectionMode mode={SelectionMode.Text} />
                  <SwitchSelectionMode mode={SelectionMode.Hand} />
                </ToolbarGroup>
              </Toolbar>
            )
          }}
        </ToolbarComponent>
      ),
    })

    const srcUrl = domNode.attribs?.src || ''

    // Convert base64 data URL to a Blob URL for compatibility
    const pdfUrl = useMemo(() => {
      if (srcUrl.startsWith('data:application/pdf;base64,')) {
        const base64 = srcUrl.replace('data:application/pdf;base64,', '')
        const binaryString = atob(base64)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }
        const blob = new Blob([bytes], { type: 'application/pdf' })
        return URL.createObjectURL(blob)
      }

      return srcUrl
    }, [srcUrl])

    return (
      <Container height={height} role="region" aria-label={`PDF viewer: ${fileName}`}>
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <Viewer
            fileUrl={pdfUrl}
            defaultScale={SpecialZoomLevel.PageWidth}
            plugins={[defaultLayoutPluginInstance]}
            transformGetDocumentParams={options => ({
              ...options,
              isEvalSupported: false
            })}
          />
        </Worker>
      </Container>
    )
  },
)

export default AccessiblePDFViewer
